import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

type KenwoodCatKind =
  | 'tmv7'
  | 'thg71'
  | 'thf6'
  | 'd710'
  | 'd72'
  | 'd74'
  | 'thk2'
  | 'tm271'
  | 'ts2000'
  | 'ts480'
  | 'ts590'

type KenwoodCatSpec = {
  id: string
  model: string
  summary: string
  kind: KenwoodCatKind
  channelCount: number
  nameLength: number
  idCandidates: readonly string[]
  modes: readonly string[]
}

export type KenwoodCatProfile = {
  protocol: 'kenwood-cat'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: number
  memorySize: number
  kind: KenwoodCatKind
  idCandidates: readonly string[]
  modes: readonly string[]
  powerLevels: readonly ['Radio']
}

const KENWOOD_TONES: readonly number[] = (CTCSS_TONES as readonly number[]).filter(
  (tone) =>
    ![159.8, 165.5, 171.3, 177.3, 183.5, 189.9, 196.6, 199.5].includes(tone),
)
const OLD_TONES: readonly number[] = (CTCSS_TONES as readonly number[]).filter(
  (tone) =>
    ![
      159.8, 165.5, 171.3, 177.3, 183.5, 189.9, 196.6, 199.5, 206.5, 229.1,
      254.1,
    ].includes(tone),
)
const THG71_TONES: readonly number[] = OLD_TONES.filter((tone) => tone !== 69.3)
const KENWOOD_TONES_1750 = [...KENWOOD_TONES, 1750]
const D710_STEPS = [5, 6.25, 8.33, 10, 12.5, 15, 20, 25, 30, 50, 100]
const THF6_STEPS = [5, 6.25, 8.33, 9, 10, 12.5, 15, 20, 25, 30, 50, 100]
const THG71_STEPS = [5, 6.25, 10, 12.5, 15, 20, 25, 30, 50, 100]

const SPECS: readonly KenwoodCatSpec[] = [
  {
    id: 'kenwood-tm-v7',
    model: 'TM-V7',
    summary: 'Kenwood TM-V7 dual-band mobile using Kenwood live CAT commands.',
    kind: 'tmv7',
    channelCount: 200,
    nameLength: 7,
    idCandidates: ['TM-V7'],
    modes: ['FM'],
  },
  {
    id: 'kenwood-tm-g707',
    model: 'TM-G707',
    summary: 'Kenwood TM-G707 mobile using live memory CAT commands.',
    kind: 'tmv7',
    channelCount: 180,
    nameLength: 7,
    idCandidates: ['TM-G707'],
    modes: ['FM'],
  },
  {
    id: 'kenwood-th-g71',
    model: 'TH-G71',
    summary: 'Kenwood TH-G71 handheld with split receive and transmit frequencies.',
    kind: 'thg71',
    channelCount: 200,
    nameLength: 6,
    idCandidates: ['TH-G71'],
    modes: ['FM'],
  },
  {
    id: 'kenwood-th-f6',
    model: 'TH-F6',
    summary: 'Kenwood TH-F6 wideband handheld using live CAT memory.',
    kind: 'thf6',
    channelCount: 400,
    nameLength: 8,
    idCandidates: ['TH-F6'],
    modes: ['FM', 'WFM', 'AM', 'LSB', 'USB', 'CW', 'NFM'],
  },
  {
    id: 'kenwood-th-f7',
    model: 'TH-F7',
    summary: 'Kenwood TH-F7 wideband handheld using the TH-F6 CAT protocol.',
    kind: 'thf6',
    channelCount: 400,
    nameLength: 8,
    idCandidates: ['TH-F7'],
    modes: ['FM', 'WFM', 'AM', 'LSB', 'USB', 'CW', 'NFM'],
  },
  {
    id: 'kenwood-tm-d710',
    model: 'TM-D710',
    summary: 'Kenwood TM-D710 mobile with 1,000 live memory channels.',
    kind: 'd710',
    channelCount: 1000,
    nameLength: 8,
    idCandidates: ['TM-D710'],
    modes: ['FM', 'NFM', 'AM'],
  },
  {
    id: 'kenwood-th-d72-live',
    model: 'TH-D72 (live mode)',
    summary: 'Kenwood TH-D72 live memory protocol.',
    kind: 'd72',
    channelCount: 1000,
    nameLength: 8,
    idCandidates: ['TH-D72'],
    modes: ['FM', 'NFM', 'AM'],
  },
  {
    id: 'kenwood-th-d74-live',
    model: 'TH-D74 (live mode)',
    summary: 'Kenwood TH-D74 channel protocol; names are not exposed by CAT.',
    kind: 'd74',
    channelCount: 1000,
    nameLength: 0,
    idCandidates: ['TH-D74'],
    modes: ['FM', 'NFM', 'AM', 'DV', 'DR', 'LSB', 'USB', 'CW', 'WFM', 'R-CW'],
  },
  {
    id: 'kenwood-tm-v71',
    model: 'TM-V71',
    summary: 'Kenwood TM-V71 mobile using the TM-D710 memory protocol.',
    kind: 'd710',
    channelCount: 1000,
    nameLength: 8,
    idCandidates: ['TM-V71'],
    modes: ['FM', 'NFM', 'AM'],
  },
  {
    id: 'kenwood-tm-d710g',
    model: 'TM-D710G',
    summary: 'Kenwood TM-D710G mobile with live memory CAT programming.',
    kind: 'd710',
    channelCount: 1000,
    nameLength: 8,
    idCandidates: ['TM-D710G', 'TM-D710'],
    modes: ['FM', 'NFM', 'AM'],
  },
  {
    id: 'kenwood-th-k2',
    model: 'TH-K2',
    summary: 'Kenwood TH-K2 2-meter handheld live memory protocol.',
    kind: 'thk2',
    channelCount: 100,
    nameLength: 6,
    idCandidates: ['TH-K2'],
    modes: ['FM', 'NFM'],
  },
  {
    id: 'kenwood-tm-271',
    model: 'TM-271',
    summary: 'Kenwood TM-271 2-meter mobile live memory protocol.',
    kind: 'tm271',
    channelCount: 200,
    nameLength: 6,
    idCandidates: ['TM-271'],
    modes: ['FM', 'NFM'],
  },
  {
    id: 'kenwood-tm-281',
    model: 'TM-281',
    summary: 'Kenwood TM-281 2-meter mobile live memory protocol.',
    kind: 'tm271',
    channelCount: 200,
    nameLength: 6,
    idCandidates: ['TM-281'],
    modes: ['FM', 'NFM'],
  },
  {
    id: 'kenwood-tm-471',
    model: 'TM-471',
    summary: 'Kenwood TM-471 70-centimeter mobile live memory protocol.',
    kind: 'tm271',
    channelCount: 200,
    nameLength: 6,
    idCandidates: ['TM-471'],
    modes: ['FM', 'NFM'],
  },
  {
    id: 'kenwood-ts-590-live',
    model: 'TS-590S/SG_LiveMode',
    summary: 'Kenwood TS-590 live CAT memories with HF and 6-meter modes.',
    kind: 'ts590',
    channelCount: 100,
    nameLength: 8,
    idCandidates: ['TS-590', 'ID021', 'ID023'],
    modes: ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R', 'Data+LSB', 'Data+USB', 'Data+FM'],
  },
  {
    id: 'kenwood-ts-480-live',
    model: 'TS-480_LiveMode',
    summary: 'Kenwood TS-480 live CAT memories with HF and 6-meter modes.',
    kind: 'ts480',
    channelCount: 100,
    nameLength: 8,
    idCandidates: ['TS-480', 'ID020'],
    modes: ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R'],
  },
  {
    id: 'kenwood-ts-2000',
    model: 'TS-2000',
    summary: 'Kenwood TS-2000 HF/VHF/UHF live memory CAT driver.',
    kind: 'ts2000',
    channelCount: 290,
    nameLength: 7,
    idCandidates: ['TS-2000', 'ID019'],
    modes: ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CWR', 'FSKR'],
  },
  {
    id: 'kenwood-ts-480-clone',
    model: 'TS-480_CloneMode',
    summary: 'Kenwood TS-480 CAT-backed memory editor.',
    kind: 'ts480',
    channelCount: 100,
    nameLength: 8,
    idCandidates: ['TS-480', 'ID020'],
    modes: ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R'],
  },
  {
    id: 'kenwood-ts-590sg-clone',
    model: 'TS-590SG_CloneMode',
    summary: 'Kenwood TS-590SG CAT-backed memory editor.',
    kind: 'ts590',
    channelCount: 100,
    nameLength: 8,
    idCandidates: ['TS-590SG', 'ID023', 'TS-590'],
    modes: ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R', 'Data+LSB', 'Data+USB', 'Data+FM'],
  },
  {
    id: 'kenwood-ts-590s-clone',
    model: 'TS-590S_CloneMode',
    summary: 'Kenwood TS-590S CAT-backed memory editor.',
    kind: 'ts590',
    channelCount: 100,
    nameLength: 8,
    idCandidates: ['TS-590S', 'ID021', 'TS-590'],
    modes: ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R', 'Data+LSB', 'Data+USB', 'Data+FM'],
  },
]

const IMAGE_PREFIX = new TextEncoder().encode('NEWCHIRP-KENWOOD-CAT\n')
const IMAGE_BYTES_PER_CHANNEL = 320

export const KENWOOD_CAT_PROFILES: readonly KenwoodCatProfile[] = SPECS.map(
  (spec) => ({
    protocol: 'kenwood-cat',
    id: spec.id,
    vendor: 'Kenwood',
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      `${spec.channelCount} CAT channels`,
      spec.nameLength ? `${spec.nameLength}-character names` : 'Frequency-only channels',
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channelCount,
    nameLength: spec.nameLength,
    memorySize: IMAGE_PREFIX.byteLength +
      spec.channelCount * IMAGE_BYTES_PER_CHANNEL,
    kind: spec.kind,
    idCandidates: spec.idCandidates,
    modes: spec.modes,
    powerLevels: ['Radio'],
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Radio',
    skip: false,
  }
}

function radioNumber(profile: KenwoodCatProfile, number: number): number {
  return number - 1
}

function intField(value: string | undefined, fallback = 0): number {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

function hexField(value: string | undefined, fallback = 0): number {
  const parsed = Number.parseInt(value ?? '', 16)
  return Number.isFinite(parsed) ? parsed : fallback
}

function toneChannel(
  channel: WebChannel,
  mode: string,
  tone: number | undefined,
  dtcs: number | undefined,
): WebChannel {
  if (mode === 'Tone' && tone !== undefined) {
    return { ...channel, toneMode: 'ctcss', ctcss: tone, dtcs: null }
  }
  if (mode === 'TSQL' && tone !== undefined) {
    return { ...channel, toneMode: 'ctcss', ctcss: tone, dtcs: null }
  }
  if (mode === 'DTCS' && dtcs !== undefined) {
    return {
      ...channel,
      toneMode: 'dtcs',
      ctcss: null,
      dtcs,
      dtcsPolarity: 'N',
    }
  }
  return { ...channel, toneMode: 'none', ctcss: null, dtcs: null }
}

function parseTmv7(
  response: string,
  profile: KenwoodCatProfile,
  number: number,
): WebChannel {
  if (['N', 'E', '?', ''].includes(response.trim())) return emptyChannel(number)
  const value = response.includes(' ') ? response.split(' ').slice(1).join(' ') : response
  const fields = value.split(',')
  const receive = intField(fields[3])
  if (!receive) return emptyChannel(number)
  const duplex = intField(fields[5])
  const offset = intField(fields[10])
  const transmit =
    duplex === 1
      ? receive + offset
      : duplex === 2
        ? receive - offset
        : receive
  const tmode = intField(fields[7]) ? 'Tone' : intField(fields[8]) ? 'TSQL' : ''
  const rtone = OLD_TONES[intField(fields[7], 1) - 1]
  const ctone = OLD_TONES[intField(fields[9], 1) - 1]
  return toneChannel(
    {
      number,
      empty: false,
      name: '',
      rxFrequency: receive,
      txFrequency: transmit,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
      mode: 'FM',
      power: 'Radio',
      skip: false,
      tuningStep: 5,
    },
    tmode,
    tmode === 'TSQL' ? ctone : rtone,
    undefined,
  )
}

function parseThg71(
  response: string,
  splitResponse: string | null,
  profile: KenwoodCatProfile,
  number: number,
): WebChannel {
  const fields = response.includes(' ')
    ? response.split(' ').slice(1).join(' ').split(',')
    : response.split(',')
  const receive = intField(fields[3])
  if (!receive) return emptyChannel(number)
  const duplex = intField(fields[5])
  let offset = intField(fields[13])
  if (splitResponse) {
    const splitFields = splitResponse.includes(' ')
      ? splitResponse.split(' ').slice(1).join(' ').split(',')
      : splitResponse.split(',')
    offset = intField(splitFields[3])
    return toneChannel(
      {
        number,
        empty: false,
        name: '',
        rxFrequency: receive,
        txFrequency: offset || receive,
        mode: 'FM',
        power: 'Radio',
        skip: intField(fields[14]) !== 0,
        tuningStep: THG71_STEPS[hexField(fields[4])] ?? 5,
        toneMode: 'none',
        ctcss: null,
        dtcs: null,
        dtcsPolarity: 'N',
      },
      intField(fields[7]) ? 'Tone' : intField(fields[8]) ? 'TSQL' : '',
      intField(fields[7])
        ? THG71_TONES[intField(fields[10], 1) - 1]
        : THG71_TONES[intField(fields[12], 1) - 1],
      undefined,
    )
  }
  const transmit =
    duplex === 1 ? receive + offset : duplex === 2 ? receive - offset : receive
  return toneChannel(
    {
      number,
      empty: false,
      name: '',
      rxFrequency: receive,
      txFrequency: transmit,
      mode: 'FM',
      power: 'Radio',
      skip: intField(fields[14]) !== 0,
      tuningStep: THG71_STEPS[hexField(fields[4])] ?? 5,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    intField(fields[7]) ? 'Tone' : intField(fields[8]) ? 'TSQL' : '',
    intField(fields[7])
      ? THG71_TONES[intField(fields[10], 1) - 1]
      : THG71_TONES[intField(fields[12], 1) - 1],
    undefined,
  )
}

function parseThf6(
  response: string,
  splitResponse: string | null,
  profile: KenwoodCatProfile,
  number: number,
): WebChannel {
  const fields = response.includes(' ')
    ? response.split(' ').slice(1).join(' ').split(',')
    : response.split(',')
  const receive = intField(fields[2])
  if (!receive) return emptyChannel(number)
  const duplex = intField(fields[4])
  let txFrequency = receive
  if (splitResponse) {
    const splitFields = splitResponse.includes(' ')
      ? splitResponse.split(' ').slice(1).join(' ').split(',')
      : splitResponse.split(',')
    txFrequency = intField(splitFields[2]) || receive
  } else {
    const offset = intField(fields[12])
    txFrequency =
      duplex === 1
        ? receive + offset
        : duplex === 2
          ? receive - offset
          : receive
  }
  const tmode = intField(fields[6])
    ? 'Tone'
    : intField(fields[7])
      ? 'TSQL'
      : intField(fields[8])
        ? 'DTCS'
        : ''
  return toneChannel(
    {
      number,
      empty: false,
      name: '',
      rxFrequency: receive,
      txFrequency,
      mode: ['FM', 'WFM', 'AM', 'LSB', 'USB', 'CW'][intField(fields[13])] ?? 'FM',
      power: 'Radio',
      skip: fields[14] === '1',
      tuningStep: THF6_STEPS[hexField(fields[3])] ?? 5,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    tmode,
    KENWOOD_TONES[intField(fields[9])],
    DTCS_CODES[intField(fields[11])],
  )
}

function parseD710(
  response: string,
  profile: KenwoodCatProfile,
  number: number,
  variant: 'd710' | 'd72',
): WebChannel {
  if (['N', 'E', '?', ''].includes(response.trim())) return emptyChannel(number)
  const fields = response.includes(' ')
    ? response.split(' ').slice(1).join(' ').split(',')
    : response.split(',')
  const receive = intField(fields[1])
  if (!receive) return emptyChannel(number)
  const d72Offset = variant === 'd72' ? 2 : 0
  const duplexIndex = 3
  const toneBase = 5
  const rtoneIndex = toneBase + 3 + d72Offset
  const ctoneIndex = rtoneIndex + 1
  const dtcsIndex = ctoneIndex + 1
  const modeIndex = 12 + d72Offset
  const splitIndex = 13 + d72Offset
  const skipIndex = 15 + d72Offset
  const duplex = intField(fields[duplexIndex])
  const offset = intField(fields[11 + d72Offset])
  const split = intField(fields[splitIndex])
  const txFrequency = split || duplex === 3 ? split || offset : duplex === 1 ? receive + offset : duplex === 2 ? receive - offset : receive
  const tmode = intField(fields[toneBase])
    ? 'Tone'
    : intField(fields[toneBase + 1])
      ? 'TSQL'
      : intField(fields[toneBase + 2])
        ? 'DTCS'
        : ''
  return toneChannel(
    {
      number,
      empty: false,
      name: '',
      rxFrequency: receive,
      txFrequency,
      mode: ['FM', 'NFM', 'AM'][intField(fields[modeIndex])] ?? 'FM',
      power: 'Radio',
      skip: intField(fields[skipIndex]) !== 0,
      tuningStep: D710_STEPS[hexField(fields[2])] ?? 5,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    tmode,
    tmode === 'TSQL'
      ? KENWOOD_TONES[intField(fields[ctoneIndex])]
      : KENWOOD_TONES[intField(fields[rtoneIndex])],
    DTCS_CODES[intField(fields[dtcsIndex])],
  )
}

function parseD74(
  response: string,
  profile: KenwoodCatProfile,
  number: number,
): WebChannel {
  if (['N', 'E', '?', ''].includes(response.trim())) return emptyChannel(number)
  const fields = response.includes(' ')
    ? response.split(' ').slice(1).join(' ').split(',')
    : response.split(',')
  const receive = intField(fields[1])
  if (!receive) return emptyChannel(number)
  const duplex = intField(fields[14])
  const offset = intField(fields[2])
  const txFrequency =
    duplex === 3 ? offset : duplex === 1 ? receive + offset : duplex === 2 ? receive - offset : receive
  const tmode = intField(fields[8])
    ? 'Tone'
    : intField(fields[9])
      ? 'TSQL'
      : intField(fields[10])
        ? 'DTCS'
        : ''
  const modes = ['FM', 'DV', 'AM', 'LSB', 'USB', 'CW', 'NFM', 'DR', 'WFM', 'R-CW']
  return toneChannel(
    {
      number,
      empty: false,
      name: '',
      rxFrequency: receive,
      txFrequency,
      mode: modes[intField(fields[5])] ?? 'FM',
      power: 'Radio',
      skip: intField(fields[22]) !== 0,
      tuningStep: 5,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    tmode,
    tmode === 'TSQL'
      ? KENWOOD_TONES[intField(fields[16])]
      : KENWOOD_TONES[intField(fields[15])],
    DTCS_CODES[intField(fields[17])],
  )
}

function parseThk2(
  response: string,
  profile: KenwoodCatProfile,
  number: number,
): WebChannel {
  if (['N', 'E', '?', ''].includes(response.trim())) return emptyChannel(number)
  const fields = response.includes(' ')
    ? response.split(' ').slice(1).join(' ').split(',')
    : response.split(',')
  const receive = intField(fields[1])
  if (!receive) return emptyChannel(number)
  const duplex = intField(fields[3])
  const offset = intField(fields[11])
  const tmode = intField(fields[5])
    ? 'Tone'
    : intField(fields[6])
      ? 'TSQL'
      : intField(fields[7])
        ? 'DTCS'
        : ''
  return toneChannel(
    {
      number,
      empty: false,
      name: '',
      rxFrequency: receive,
      txFrequency:
        duplex === 1
          ? receive + offset
          : duplex === 2
            ? receive - offset
            : receive,
      mode: ['FM', 'NFM'][intField(fields[12])] ?? 'FM',
      power: 'Radio',
      skip: intField(fields[16]) !== 0,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    tmode,
    tmode === 'TSQL'
      ? KENWOOD_TONES[intField(fields[9])]
      : KENWOOD_TONES[intField(fields[8])],
    DTCS_CODES[intField(fields[10])],
  )
}

function parseTs2000(response: string, number: number): WebChannel {
  if (['N', 'E', '?', ''].includes(response.trim())) return emptyChannel(number)
  const spec = ` ${response}`
  const receive = intField(spec.slice(7, 18))
  if (!receive) return emptyChannel(number)
  const mode = ['?', 'LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CWR', '?', 'FSKR'][intField(spec.slice(18, 19))] ?? 'FM'
  const tmode = ['', 'Tone', 'TSQL', 'DTCS'][intField(spec.slice(20, 21))] ?? ''
  const rtone = OLD_TONES.filter((tone) => tone !== 69.3)[intField(spec.slice(21, 23), 1) - 1]
  const ctone = OLD_TONES.filter((tone) => tone !== 69.3)[intField(spec.slice(23, 25), 1) - 1]
  const dtcs = DTCS_CODES[intField(spec.slice(25, 28))]
  const duplex = intField(spec.slice(29, 30))
  const offset = intField(spec.slice(30, 39))
  const txFrequency =
    duplex === 1
      ? receive + offset
      : duplex === 2
        ? receive - offset
        : receive
  return toneChannel(
    {
      number,
      empty: false,
      name: spec.slice(42, 49).trim(),
      rxFrequency: receive,
      txFrequency,
      mode,
      power: 'Radio',
      skip: intField(spec.slice(19, 20)) !== 0,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    tmode,
    tmode === 'TSQL' ? ctone : rtone,
    dtcs,
  )
}

function tsModeIndex(mode: string, modes: readonly string[]): number {
  const index = modes.indexOf(mode)
  if (index < 0) return 0
  return index + 1 > 7 ? index + 2 : index + 1
}

function parseTs480(
  receiveResponse: string,
  splitResponse: string,
  number: number,
): WebChannel {
  const receive = intField(`x${receiveResponse}`.slice(7, 18))
  if (!receive) return emptyChannel(number)
  const modeCode = intField(`x${receiveResponse}`.slice(18, 19), 1)
  const modes = ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R']
  const modeIndex = modeCode === 9 ? 7 : Math.max(0, modeCode - 1)
  const skip = intField(`x${receiveResponse}`.slice(19, 20)) !== 0
  const tmode = intField(`x${receiveResponse}`.slice(20, 21))
  const rtoneIndex = intField(`x${receiveResponse}`.slice(21, 23), 8)
  const ctoneIndex = intField(`x${receiveResponse}`.slice(23, 25), 8)
  const tx = intField(`x${splitResponse}`.slice(7, 18))
  return toneChannel(
    {
      number,
      empty: false,
      name: `x${receiveResponse}`.slice(41, 50).trim(),
      rxFrequency: receive,
      txFrequency: tx && tx !== receive ? tx : receive,
      mode: modes[modeIndex] ?? 'FM',
      power: 'Radio',
      skip,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    ['', 'Tone', 'TSQL', 'Cross'][tmode] ?? '',
    tmode === 2 ? KENWOOD_TONES_1750[ctoneIndex] : KENWOOD_TONES_1750[rtoneIndex],
    undefined,
  )
}

function parseTs590(
  receiveResponse: string,
  splitResponse: string,
  number: number,
): WebChannel {
  const receive = intField(`x${receiveResponse}`.slice(7, 18))
  if (!receive) return emptyChannel(number)
  const modeCode = intField(`x${receiveResponse}`.slice(18, 19), 1)
  const dataMode = intField(`x${receiveResponse}`.slice(19, 20))
  const toneMode = intField(`x${receiveResponse}`.slice(20, 21))
  const rtoneIndex = intField(`x${receiveResponse}`.slice(21, 23), 8)
  const ctoneIndex = intField(`x${receiveResponse}`.slice(23, 25), 8)
  const modes = ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R']
  let modeIndex = modeCode === 9 ? 7 : Math.max(0, modeCode - 1)
  if (dataMode && modeCode === 1) modeIndex = 8
  if (dataMode && modeCode === 2) modeIndex = 9
  if (dataMode && modeCode === 4) modeIndex = 10
  const mode = [...modes, 'Data+LSB', 'Data+USB', 'Data+FM'][modeIndex] ?? 'FM'
  const tx = intField(`x${splitResponse}`.slice(7, 18))
  return toneChannel(
    {
      number,
      empty: false,
      name: `x${receiveResponse}`.slice(41, 49).trim(),
      rxFrequency: receive,
      txFrequency: tx || receive,
      mode,
      power: 'Radio',
      skip: intField(`x${receiveResponse}`.slice(40, 41)) !== 0,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
    },
    ['', 'Tone', 'TSQL', 'Cross'][toneMode] ?? '',
    toneMode === 2 ? KENWOOD_TONES_1750[ctoneIndex] : KENWOOD_TONES_1750[rtoneIndex],
    undefined,
  )
}

function encodeChannels(
  profile: KenwoodCatProfile,
  channels: readonly WebChannel[],
): Uint8Array {
  const payload = new TextEncoder().encode(JSON.stringify(channels))
  const image = new Uint8Array(profile.memorySize)
  image.set(IMAGE_PREFIX)
  image.set(payload, IMAGE_PREFIX.byteLength)
  return image
}

function decodeChannels(
  image: Uint8Array,
  profile: KenwoodCatProfile,
): WebChannel[] {
  const prefix = String.fromCharCode(...image.slice(0, IMAGE_PREFIX.byteLength))
  if (prefix !== new TextDecoder().decode(IMAGE_PREFIX)) {
    return Array.from({ length: profile.channelMaximum }, (_, index) =>
      emptyChannel(index + 1),
    )
  }
  const payload = new TextDecoder()
    .decode(image.slice(IMAGE_PREFIX.byteLength))
    .replace(/\u0000+$/g, '')
  try {
    const channels = JSON.parse(payload) as WebChannel[]
    const byNumber = new Map(channels.map((channel) => [channel.number, channel]))
    return Array.from(
      { length: profile.channelMaximum },
      (_, index) => byNumber.get(index + 1) ?? emptyChannel(index + 1),
    )
  } catch {
    return Array.from({ length: profile.channelMaximum }, (_, index) =>
      emptyChannel(index + 1),
    )
  }
}

export function parseKenwoodCatChannels(
  image: Uint8Array,
  profile: KenwoodCatProfile,
): WebChannel[] {
  return decodeChannels(image, profile)
}

export function applyKenwoodCatChannel(
  image: Uint8Array,
  profile: KenwoodCatProfile,
  channel: WebChannel,
): void {
  const channels = decodeChannels(image, profile)
  channels[channel.number - 1] = channel
  image.set(encodeChannels(profile, channels))
}

async function readTerminated(
  transport: RadioTransport,
  delimiter: number,
  timeoutMs = 1800,
): Promise<string> {
  if (transport.readUntil) {
    const bytes = await transport.readUntil(delimiter, timeoutMs)
    return new TextDecoder().decode(bytes.slice(0, -1)).trim()
  }
  const bytes: number[] = []
  while (bytes.length < 4096) {
    const byte = (await transport.readExact(1, timeoutMs))[0]
    if (byte === delimiter) break
    bytes.push(byte)
  }
  return new TextDecoder().decode(Uint8Array.from(bytes)).trim()
}

async function command(
  transport: RadioTransport,
  text: string,
  terminator: '\r' | ';',
  response = true,
): Promise<string> {
  await transport.write(new TextEncoder().encode(`${text}${terminator}`))
  if (!response) {
    await Promise.resolve()
    return ''
  }
  return readTerminated(transport, terminator.charCodeAt(0))
}

function ctrlTerminator(profile: KenwoodCatProfile): '\r' | ';' {
  return profile.kind === 'ts2000' || profile.kind === 'ts480' || profile.kind === 'ts590'
    ? ';'
    : '\r'
}

async function enter(
  profile: KenwoodCatProfile,
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  const terminator = ctrlTerminator(profile)
  const identity = await command(transport, 'ID', terminator)
  if (
    identity &&
    !profile.idCandidates.some((candidate) =>
      identity.toUpperCase().includes(candidate.toUpperCase()),
    )
  ) {
    throw new Error(
      `${profile.model} expects ${profile.idCandidates.join(' or ')}, but the radio reported ${identity}.`,
    )
  }
  await command(transport, terminator === ';' ? 'AI0' : 'AI 0', terminator)
  return identity || profile.model
}

async function readChannel(
  profile: KenwoodCatProfile,
  transport: RadioTransport,
  number: number,
): Promise<WebChannel> {
  const terminator = ctrlTerminator(profile)
  const radio = radioNumber(profile, number)
  let response = ''
  let splitResponse = ''
  if (profile.kind === 'ts2000') {
    response = await command(transport, `MR0${radio.toString().padStart(3, '0')}`, terminator)
    if (response === 'N') return emptyChannel(number)
    splitResponse = await command(transport, `MR1${radio.toString().padStart(3, '0')}`, terminator)
  } else if (profile.kind === 'ts480' || profile.kind === 'ts590') {
    const spaced = profile.kind === 'ts590' && radio < 100
    const suffix = spaced ? ` ${radio.toString().padStart(2, '0')}` : radio.toString().padStart(3, '0')
    response = await command(transport, `MR0${suffix}`, terminator)
    splitResponse = await command(transport, `MR1${suffix}`, terminator)
  } else if (profile.kind === 'd710' || profile.kind === 'd72' || profile.kind === 'd74') {
    response = await command(transport, `ME ${radio.toString().padStart(3, '0')}`, terminator)
  } else if (profile.kind === 'thk2') {
    response = await command(transport, `ME ${radio.toString().padStart(2, '0')}`, terminator)
  } else if (profile.kind === 'tm271') {
    response = await command(transport, `ME ${radio.toString().padStart(3, '0')}`, terminator)
  } else if (profile.kind === 'thf6') {
    response = await command(transport, `MR 0,${radio.toString().padStart(3, '0')}`, terminator)
  } else if (profile.kind === 'thg71') {
    response = await command(transport, `MR 0,0,${radio.toString().padStart(3, '0')}`, terminator)
  } else {
    response = await command(transport, `MR 0,0,${radio.toString().padStart(3, '0')}`, terminator)
  }
  if (['N', 'E', '?', ''].includes(response.trim())) {
    return emptyChannel(number)
  }

  let parsed: WebChannel
  if (profile.kind === 'ts2000') {
    parsed = parseTs2000(response, number)
    if (parsed.txFrequency === parsed.rxFrequency && splitResponse) {
      const splitSpec = ` ${splitResponse}`
      if (splitSpec.length >= 18) {
        const split = intField(splitSpec.slice(7, 18))
        if (split) parsed = { ...parsed, txFrequency: split }
      }
    }
  } else if (profile.kind === 'ts480') {
    parsed = parseTs480(response, splitResponse, number)
  } else if (profile.kind === 'ts590') {
    parsed = parseTs590(response, splitResponse, number)
  } else if (profile.kind === 'tmv7') {
    parsed = parseTmv7(response, profile, number)
  } else if (profile.kind === 'thg71') {
    parsed = parseThg71(response, null, profile, number)
  } else if (profile.kind === 'thf6') {
    parsed = parseThf6(response, null, profile, number)
  } else if (profile.kind === 'd72') {
    parsed = parseD710(response, profile, number, 'd72')
  } else if (profile.kind === 'd74') {
    parsed = parseD74(response, profile, number)
  } else if (profile.kind === 'd710') {
    parsed = parseD710(response, profile, number, 'd710')
  } else {
    parsed = parseThk2(response, profile, number)
  }

  if (
    !parsed.empty &&
    parsed.rxFrequency &&
    parsed.txFrequency === parsed.rxFrequency &&
    (profile.kind === 'thg71' || profile.kind === 'thf6')
  ) {
    const splitCommand =
      profile.kind === 'thg71'
        ? `MR 0,1,${radio.toString().padStart(3, '0')}`
        : `MR 1,${radio.toString().padStart(3, '0')}`
    const split = await command(transport, splitCommand, terminator)
    if (split && !['N', 'E', '?'].includes(split.trim())) {
      const fields = split.includes(' ')
        ? split.split(' ').slice(1).join(' ').split(',')
        : split.split(',')
      const splitFrequency = intField(fields[profile.kind === 'thg71' ? 3 : 2])
      if (splitFrequency) {
        parsed = { ...parsed, txFrequency: splitFrequency }
      }
    }
  }

  if (profile.nameLength && profile.kind !== 'd74') {
    const nameCommand =
      profile.kind === 'd710' || profile.kind === 'd72' || profile.kind === 'tm271' || profile.kind === 'thk2'
        ? `MN ${radio.toString().padStart(profile.kind === 'thk2' ? 2 : 3, '0')}`
        : profile.kind === 'thf6'
          ? `MNA ${radio.toString().padStart(3, '0')}`
          : `MNA 0,${radio.toString().padStart(3, '0')}`
    const nameResponse = await command(transport, nameCommand, terminator)
    if (nameResponse && !['N', 'E', '?'].includes(nameResponse.trim())) {
      const name = nameResponse.includes(' ')
        ? nameResponse.split(' ').slice(1).join(' ')
        : nameResponse
      parsed = {
        ...parsed,
        name: name.split(',').at(-1)?.trim().slice(0, profile.nameLength) ?? '',
      }
    }
  }
  return parsed
}

export async function readKenwoodCatRadioWithTransport(
  profile: KenwoodCatProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await enter(profile, transport)
  const channels: WebChannel[] = []
  for (let number = 1; number <= profile.channelMaximum; number += 1) {
    channels.push(await readChannel(profile, transport, number))
    onProgress?.({
      current: number,
      total: profile.channelMaximum,
      label: `Reading ${profile.model} CAT channels`,
    })
  }
  return { image: encodeChannels(profile, channels), reportedModel }
}

function toneFields(
  channel: WebChannel,
): { tmode: number; rtone: number; ctone: number; dtcs: number } {
  if (channel.toneMode === 'ctcss') {
    const index = KENWOOD_TONES.indexOf(channel.ctcss ?? 88.5)
    return { tmode: 1, rtone: Math.max(0, index), ctone: Math.max(0, index), dtcs: 0 }
  }
  if (channel.toneMode === 'dtcs') {
    return {
      tmode: 3,
      rtone: 0,
      ctone: 0,
      dtcs: Math.max(
        0,
        (DTCS_CODES as readonly number[]).indexOf(channel.dtcs ?? 23),
      ),
    }
  }
  return { tmode: 0, rtone: 0, ctone: 0, dtcs: 0 }
}

function flag(value: boolean): string {
  return value ? '1' : '0'
}

function standardMemoryCommand(
  profile: KenwoodCatProfile,
  channel: WebChannel,
): { main: string; name: string; split?: string } {
  const radio = radioNumber(profile, channel.number)
  const padded3 = radio.toString().padStart(3, '0')
  const padded2 = radio.toString().padStart(2, '0')
  const tones = toneFields(channel)
  const tmode =
    channel.toneMode === 'none'
      ? 0
      : channel.toneMode === 'dtcs'
        ? 3
        : 1
  const splitTx =
    channel.txFrequency &&
    channel.rxFrequency &&
    channel.txFrequency !== channel.rxFrequency
      ? channel.txFrequency
      : 0
  const offset = splitTx && channel.rxFrequency ? Math.abs(splitTx - channel.rxFrequency) : 0
  const duplex = splitTx
    ? splitTx > (channel.rxFrequency ?? 0)
      ? 1
      : 2
    : 0
  if (profile.kind === 'tmv7') {
    const spec = [
      String(Math.round((channel.rxFrequency ?? 0) / 1)),
      '0',
      String(duplex),
      '0',
      flag(tmode === 1),
      '0',
      '0',
      String(Math.max(0, OLD_TONES.indexOf(channel.ctcss ?? 88.5)) + 1).padStart(2, '0'),
      '000',
      String(Math.max(0, OLD_TONES.indexOf(channel.ctcss ?? 88.5)) + 1).padStart(2, '0'),
      '',
      '0',
    ].join(',')
    return {
      main: `MW 0,0,${padded3}${channel.empty ? '' : `,${spec}`}`,
      name: `MNA 0,${padded3},${channel.name.slice(0, 7).toUpperCase()}`,
    }
  }
  if (profile.kind === 'thg71') {
    const spec = [
      String(Math.round(channel.rxFrequency ?? 0)),
      '0',
      String(duplex),
      '0',
      flag(tmode === 1),
      '0',
      '',
      String(Math.max(0, THG71_TONES.indexOf(channel.ctcss ?? 88.5)) + 1).padStart(2, '0'),
      '',
      String(Math.max(0, THG71_TONES.indexOf(channel.ctcss ?? 88.5)) + 1).padStart(2, '0'),
      String(offset).padStart(9, '0'),
      String(channel.skip ? 1 : 0),
    ].join(',')
    return {
      main: `MW 0,0,${padded3}${channel.empty ? '' : `,${spec}`}`,
      name: `MNA 0,${padded3},${channel.name.slice(0, 6).toUpperCase()}`,
      split: splitTx ? `MW 0,1,${padded3},${String(Math.round(splitTx)).padStart(11, '0')},0` : undefined,
    }
  }
  if (profile.kind === 'thf6') {
    const spec = [
      String(Math.round(channel.rxFrequency ?? 0)),
      '0',
      String(duplex),
      '0',
      flag(tmode === 1),
      '0',
      flag(tmode === 3),
      String(tones.rtone).padStart(2, '0'),
      String(tones.ctone).padStart(2, '0'),
      String(tones.dtcs).padStart(3, '0'),
      String(offset).padStart(9, '0'),
      String(Math.max(0, ['FM', 'WFM', 'AM', 'LSB', 'USB', 'CW'].indexOf(channel.mode))),
      String(channel.skip ? 1 : 0),
    ].join(',')
    return {
      main: `MW 0,${padded3}${channel.empty ? '' : `,${spec}`}`,
      name: `MNA ${padded3},${channel.name.slice(0, 8)}`,
      split: splitTx ? `MW 1,${padded3},${String(Math.round(splitTx)).padStart(11, '0')},0` : undefined,
    }
  }
  if (profile.kind === 'd710' || profile.kind === 'd72') {
    const base = [
      String(Math.round(channel.rxFrequency ?? 0)).padStart(10, '0'),
      '0',
      String(duplex),
      '0',
      flag(tmode === 1),
      '0',
      flag(tmode === 3),
    ]
    if (profile.kind === 'd72') base.push('0')
    base.push(
      String(tones.rtone).padStart(2, '0'),
      String(tones.ctone).padStart(2, '0'),
      String(tones.dtcs).padStart(3, '0'),
    )
    if (profile.kind === 'd72') base.push('0')
    base.push(
      String(offset).padStart(8, '0'),
      String(Math.max(0, ['FM', 'NFM', 'AM'].indexOf(channel.mode))),
      String(Math.round(splitTx)).padStart(10, '0'),
      '0',
      String(channel.skip ? 1 : 0),
    )
    return {
      main: `ME ${padded3}${channel.empty ? '' : `,${base.join(',')}`}`,
      name: `MN ${padded3},${channel.name.slice(0, 8)}`,
    }
  }
  if (profile.kind === 'd74') {
    const spec = [
      String(Math.round(channel.rxFrequency ?? 0)).padStart(10, '0'),
      String(splitTx || offset).padStart(10, '0'),
      '0',
      '0',
      String(Math.max(0, ['FM', 'DV', 'AM', 'LSB', 'USB', 'CW', 'NFM', 'DR', 'WFM', 'R-CW'].indexOf(channel.mode))),
      '0',
      '0',
      flag(tmode === 1),
      '0',
      flag(tmode === 3),
      '0',
      '0',
      '0',
      String(splitTx ? 3 : duplex),
      String(tones.rtone).padStart(2, '0'),
      String(tones.ctone).padStart(2, '0'),
      String(tones.dtcs).padStart(3, '0'),
      '0',
      'CQCQCQ',
      '0',
      '00',
      String(channel.skip ? 1 : 0),
    ]
    return {
      main: `ME ${padded3}${channel.empty ? '' : `,${spec.join(',')}`}`,
      name: '',
    }
  }
  const spec = [
    String(Math.round(channel.rxFrequency ?? 0)).padStart(10, '0'),
    '0',
    String(duplex),
    '0',
    flag(tmode === 1),
    '0',
    flag(tmode === 3),
    String(tones.rtone).padStart(2, '0'),
    String(tones.ctone).padStart(2, '0'),
    String(tones.dtcs).padStart(3, '0'),
    String(offset).padStart(8, '0'),
    String(Math.max(0, ['FM', 'NFM'].indexOf(channel.mode))),
    '0',
    String(Math.round(splitTx)).padStart(10, '0'),
    '0',
    String(channel.skip ? 1 : 0),
  ].join(',')
  return {
    main: `ME ${profile.kind === 'thk2' ? padded2 : padded3}${channel.empty ? '' : `,${spec}`}`,
    name: `MN ${profile.kind === 'thk2' ? padded2 : padded3},${channel.name.slice(0, 6)}`,
  }
}

function tsModeCode(mode: string, modes: readonly string[]): number {
  const index = Math.max(0, modes.indexOf(mode))
  return index === 7 ? 9 : index + 1
}

async function writeChannel(
  profile: KenwoodCatProfile,
  transport: RadioTransport,
  channel: WebChannel,
): Promise<void> {
  const terminator = ctrlTerminator(profile)
  const radio = radioNumber(profile, channel.number)
  const padded3 = radio.toString().padStart(3, '0')
  const tones = toneFields(channel)
  const tx = channel.txFrequency || channel.rxFrequency || 0
  const splitTx = channel.txFrequency && channel.txFrequency !== channel.rxFrequency
  if (profile.kind === 'ts2000' || profile.kind === 'ts480' || profile.kind === 'ts590') {
    const modes =
      profile.kind === 'ts2000'
        ? ['?', 'LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CWR', '?', 'FSKR']
        : profile.kind === 'ts480'
          ? ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R']
          : ['LSB', 'USB', 'CW', 'FM', 'AM', 'FSK', 'CW-R', 'FSK-R', 'Data+LSB', 'Data+USB', 'Data+FM']
    const mode =
      profile.kind === 'ts2000'
        ? Math.max(0, modes.indexOf(channel.mode))
        : tsModeCode(channel.mode, modes)
    const tmode =
      channel.toneMode === 'none'
        ? 0
        : channel.toneMode === 'dtcs'
          ? 3
          : 1
    const toneIndex =
      channel.toneMode === 'ctcss'
        ? Math.max(0, (profile.kind === 'ts2000' ? OLD_TONES.filter((tone) => tone !== 69.3) : KENWOOD_TONES_1750).indexOf(channel.ctcss ?? 88.5))
        : 0
    if (profile.kind === 'ts2000') {
      const spec = [
        String(Math.round(channel.rxFrequency ?? 0)).padStart(11, '0'),
        String(mode),
        String(channel.skip ? 1 : 0),
        String(tmode),
        String(tmode ? toneIndex + 1 : 0).padStart(2, '0'),
        String(tmode ? toneIndex + 1 : 0).padStart(2, '0'),
        String(tones.dtcs).padStart(3, '0'),
        '0',
        String(splitTx ? 0 : 0),
        String(channel.rxFrequency && tx ? Math.abs(tx - channel.rxFrequency) : 0).padStart(9, '0'),
        '00',
        '0',
        channel.name.slice(0, 7),
      ].join('')
      await command(transport, `MW0${padded3}${channel.empty ? '0'.repeat(35) : spec}`, terminator, false)
      if (splitTx) {
        const splitSpec =
          String(Math.round(tx)).padStart(11, '0') +
          String(mode) +
          String(channel.skip ? 1 : 0) +
          String(tmode) +
          String(tmode ? toneIndex + 1 : 0).padStart(2, '0') +
          String(tmode ? toneIndex + 1 : 0).padStart(2, '0') +
          String(tones.dtcs).padStart(3, '0') +
          '0' +
          '0' +
          '0'.repeat(9) +
          '00' +
          '0' +
          channel.name.slice(0, 7)
        await command(transport, `MW1${padded3}${splitSpec}`, terminator, false)
      }
      return
    }
    if (profile.kind === 'ts480') {
      const base =
        String(Math.round(channel.rxFrequency ?? 0)).padStart(11, '0') +
        String(mode) +
        String(channel.skip ? 1 : 0) +
        String(tmode) +
        String(toneIndex).padStart(2, '0') +
        String(toneIndex).padStart(2, '0') +
        '0'.repeat(14) +
        '00' +
        '0' +
        channel.name.slice(0, 8).padEnd(8, ' ')
      await command(transport, `MW0${padded3}${channel.empty ? '0'.repeat(35) : base}`, terminator, false)
      if (splitTx) {
        const split = base.replace(/^\d{11}/, String(Math.round(tx)).padStart(11, '0'))
        await command(transport, `MW1${padded3}${split}`, terminator, false)
      }
      return
    }
    const spaced = radio < 100 ? ` ${radio.toString().padStart(2, '0')}` : padded3
    const base =
      String(Math.round(channel.rxFrequency ?? 0)).padStart(11, '0') +
      String(mode) +
      '0' +
      String(tmode) +
      String(toneIndex).padStart(2, '0') +
      String(toneIndex).padStart(2, '0') +
      '0000000000000' +
      '00' +
      String(channel.skip ? 1 : 0) +
      channel.name.slice(0, 8).padEnd(8, ' ')
    await command(transport, `MW0${spaced}${channel.empty ? '0'.repeat(35) : base}`, terminator, false)
    if (channel.rxFrequency) {
      await command(transport, `MW1${spaced}${base.replace(/^\d{11}/, String(Math.round(tx)).padStart(11, '0'))}`, terminator, false)
    }
    return
  }
  const commandSet = standardMemoryCommand(profile, channel)
  await command(transport, commandSet.main, terminator)
  if (!channel.empty && commandSet.name && profile.nameLength) {
    await command(transport, commandSet.name, terminator)
  }
  if (!channel.empty && commandSet.split) {
    await command(transport, commandSet.split, terminator)
  }
}

export async function writeKenwoodCatRadioWithTransport(
  profile: KenwoodCatProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  await enter(profile, transport)
  const channels = decodeChannels(image, profile)
  for (let index = 0; index < channels.length; index += 1) {
    await writeChannel(profile, transport, channels[index])
    onProgress?.({
      current: index + 1,
      total: channels.length,
      label: `Writing ${profile.model} CAT channels`,
    })
  }
}
